import React, { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { Bot, Send, Trash2, MessageSquare, Settings as SettingsIcon, Plus } from "lucide-react";
import { useConfirm } from "../components/ConfirmProvider";
import { fmtRoDate } from "../lib/dateFmt";

export default function Asistent() {
  const confirm = useConfirm();
  const { isAdmin } = useAuth();
  const [settings, setSettings] = useState({ name: "Asistent ConVic", emoji: "🤖", welcome_message: "" });
  const [sessions, setSessions] = useState([]);
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [editForm, setEditForm] = useState({ name: "", emoji: "", welcome_message: "" });
  const scrollRef = useRef(null);

  const loadSettings = async () => {
    try {
      const { data } = await api.get("/assistant/settings");
      setSettings(data);
    } catch {}
  };
  const loadSessions = async () => {
    try {
      const { data } = await api.get("/assistant/sessions");
      setSessions(data || []);
    } catch {}
  };
  const loadMessages = async (sid) => {
    if (!sid) {
      setMessages([]);
      return;
    }
    try {
      const { data } = await api.get(`/assistant/sessions/${sid}/messages`);
      setMessages(data || []);
    } catch {
      setMessages([]);
    }
  };
  useEffect(() => { loadSettings(); loadSessions(); }, []);
  useEffect(() => { loadMessages(activeSessionId); }, [activeSessionId]);
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading]);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setLoading(true);
    setInput("");
    // Optimistic user message
    setMessages((prev) => [...prev, { role: "user", content: text, ts: new Date().toISOString(), id: `local-${Date.now()}` }]);
    try {
      const { data } = await api.post("/assistant/chat", {
        session_id: activeSessionId,
        message: text,
      });
      if (!activeSessionId) setActiveSessionId(data.session_id);
      setMessages((prev) => [...prev, { role: "assistant", content: data.reply, ts: new Date().toISOString(), id: `local-${Date.now() + 1}` }]);
      loadSessions();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la apelul AI");
      setMessages((prev) => [...prev, { role: "assistant", content: "⚠️ Nu am putut răspunde. Încearcă din nou.", ts: new Date().toISOString(), id: `err-${Date.now()}`, error: true }]);
    } finally {
      setLoading(false);
    }
  };

  const newChat = () => {
    setActiveSessionId(null);
    setMessages([]);
  };

  const deleteSession = async (sid) => {
    if (!(await confirm({ body: "Ștergi această conversație?", danger: true }))) return;
    try {
      await api.delete(`/assistant/sessions/${sid}`);
      if (sid === activeSessionId) {
        setActiveSessionId(null);
        setMessages([]);
      }
      loadSessions();
    } catch {
      toast.error("Eroare ștergere");
    }
  };

  const openSettings = () => {
    setEditForm({ name: settings.name, emoji: settings.emoji, welcome_message: settings.welcome_message || "" });
    setSettingsOpen(true);
  };

  const saveSettings = async () => {
    try {
      const { data } = await api.put("/assistant/settings", editForm);
      setSettings(data);
      toast.success(`Agentul se numește acum «${data.name}»`);
      setSettingsOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare salvare");
    }
  };

  const suggestions = [
    "Câți angajați activi avem?",
    "Ce mașini au MFK expirat?",
    "Cine e în concediu săptămâna asta?",
    "Ce proiecte sunt în lucru?",
    "Ce articole sunt în stoc minim?",
  ];

  return (
    <div className="flex h-[calc(100vh-100px)] gap-3" data-testid="asistent-page">
      {/* Sidebar conversations */}
      <aside className="w-64 card-flat p-3 flex flex-col">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display font-bold text-sm flex items-center gap-2">
            <MessageSquare size={16}/> Conversații
          </h3>
          <button onClick={newChat} className="p-1.5 rounded hover:bg-zinc-100" title="Nouă" data-testid="new-chat-btn">
            <Plus size={16}/>
          </button>
        </div>
        <div className="flex-1 overflow-y-auto space-y-1">
          {sessions.length === 0 && (
            <div className="text-xs text-zinc-400 italic py-4 text-center">Nicio conversație. Începe scriind o întrebare.</div>
          )}
          {sessions.map((s) => (
            <div
              key={s.id}
              className={`group flex items-start gap-1 p-2 rounded text-xs cursor-pointer transition-colors ${activeSessionId === s.id ? "bg-[var(--primary-subtle)] text-[var(--primary)]" : "hover:bg-zinc-50"}`}
              data-testid={`session-${s.id}`}
            >
              <button
                onClick={() => setActiveSessionId(s.id)}
                className="flex-1 min-w-0 text-left"
                title={s.title}
              >
                <div className="font-semibold truncate">{s.title}</div>
                <div className="text-[10px] text-zinc-500 mt-0.5">
                  {s.message_count} mesaje · {fmtRoDate(s.updated_at)}
                </div>
              </button>
              <button
                onClick={() => deleteSession(s.id)}
                className="opacity-0 group-hover:opacity-100 text-red-500 p-1 hover:bg-red-50 rounded"
                title="Șterge"
              >
                <Trash2 size={12}/>
              </button>
            </div>
          ))}
        </div>
      </aside>

      {/* Chat panel */}
      <main className="flex-1 card-flat p-0 flex flex-col">
        <div className="p-3 border-b border-[var(--border)] flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-2xl">{settings.emoji || "🤖"}</span>
            <div>
              <div className="font-display font-bold text-base" data-testid="agent-name">{settings.name}</div>
              <div className="text-[11px] text-zinc-500">Asistent virtual cu acces la datele platformei</div>
            </div>
          </div>
          {isAdmin && (
            <button
              onClick={openSettings}
              className="p-2 rounded hover:bg-zinc-100"
              title="Setează numele agentului"
              data-testid="agent-settings-btn"
            >
              <SettingsIcon size={16}/>
            </button>
          )}
        </div>

        <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="chat-messages">
          {messages.length === 0 && (
            <div className="text-center py-12">
              <Bot className="mx-auto mb-3 text-zinc-300" size={48}/>
              <div className="text-sm text-zinc-600 mb-4 max-w-md mx-auto">{settings.welcome_message || "Salut! Întreabă-mă orice."}</div>
              <div className="flex flex-wrap gap-2 justify-center max-w-2xl mx-auto">
                {suggestions.map((s) => (
                  <button
                    key={s}
                    onClick={() => setInput(s)}
                    className="text-[11px] px-3 py-1.5 rounded-full border border-[var(--border)] hover:border-[var(--primary)] hover:bg-[var(--primary-subtle)]"
                    data-testid={`suggestion-${s.length}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m) => (
            <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm whitespace-pre-wrap ${
                m.role === "user"
                  ? "bg-[var(--primary)] text-white rounded-br-sm"
                  : m.error ? "bg-red-50 text-red-700 border border-red-200 rounded-bl-sm" : "bg-zinc-100 text-zinc-900 rounded-bl-sm"
              }`} data-testid={`msg-${m.role}`}>
                {m.content}
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex justify-start">
              <div className="bg-zinc-100 text-zinc-500 px-4 py-2.5 rounded-2xl rounded-bl-sm text-sm flex items-center gap-2">
                <span className="inline-block w-2 h-2 rounded-full bg-zinc-400 animate-bounce" style={{ animationDelay: "0ms" }}/>
                <span className="inline-block w-2 h-2 rounded-full bg-zinc-400 animate-bounce" style={{ animationDelay: "150ms" }}/>
                <span className="inline-block w-2 h-2 rounded-full bg-zinc-400 animate-bounce" style={{ animationDelay: "300ms" }}/>
              </div>
            </div>
          )}
        </div>

        <div className="p-3 border-t border-[var(--border)] flex gap-2">
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder="Întreabă orice despre platformă…"
            disabled={loading}
            data-testid="chat-input"
          />
          <Button onClick={send} disabled={loading || !input.trim()} className="bg-[var(--primary)]" data-testid="chat-send">
            <Send size={16}/>
          </Button>
        </div>
      </main>

      {/* Settings dialog */}
      {settingsOpen && (
        <Dialog open onOpenChange={(v) => !v && setSettingsOpen(false)}>
          <DialogContent className="max-w-md" data-testid="agent-settings-dialog">
            <DialogHeader><DialogTitle>Personalizează agentul</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="small-label">Nume agent</Label>
                <Input
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  placeholder="ex: Bossy, Mihai, Bossman, MetaConVic"
                  className="mt-1"
                  data-testid="agent-name-input"
                />
              </div>
              <div>
                <Label className="small-label">Emoji</Label>
                <Input
                  value={editForm.emoji}
                  onChange={(e) => setEditForm({ ...editForm, emoji: e.target.value })}
                  placeholder="🤖 🧠 🦾 ✨"
                  className="mt-1"
                  maxLength={4}
                />
              </div>
              <div>
                <Label className="small-label">Mesaj de bun-venit</Label>
                <Input
                  value={editForm.welcome_message}
                  onChange={(e) => setEditForm({ ...editForm, welcome_message: e.target.value })}
                  placeholder="Salut! Sunt aici să te ajut..."
                  className="mt-1"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setSettingsOpen(false)}>Anulează</Button>
              <Button onClick={saveSettings} className="bg-[var(--primary)]" data-testid="agent-save">Salvează</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
